import { Flex } from "@mantine/core";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { RoleComparePage } from "@/components/features/RolesPermissions/RoleCompare/RoleComparePage";
import type { RoleCompareSelection } from "@/components/features/RolesPermissions/RoleCompare/RoleComparePage";
import { COMPARE_BREADCRUMB } from "@/components/features/RolesPermissions/RoleCompare/roleCompareCopy";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import styles from "./roles-permissions.module.scss";

const compareSearchSchema = z.object({
  role: z.string().optional().catch(undefined),
  with: z.array(z.string()).optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/roles-permissions/compare")({
  component: CompareRoles,
  validateSearch: compareSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const BREADCRUMB_ITEMS = [
  {
    title: "Roles & Permissions",
    href: "/roles-permissions",
    icon: getRouteIcon(MODULE_KEYS_MAP.rolesPermissions),
  },
  { title: COMPARE_BREADCRUMB, href: "/roles-permissions/compare" },
];

const EMPTY_IDS: string[] = [];

function CompareRoles() {
  useBreadcrumb(BREADCRUMB_ITEMS, { listView: "List View" });
  const search = useSearch({ from: "/_authenticated/roles-permissions/compare" });
  const navigate = useNavigate({ from: "/roles-permissions/compare" });

  // The picks live in the URL, so a comparison can be linked; replace keeps Back on the list.
  const handleSelectionChange = ({ roleId, compareWithIds }: RoleCompareSelection) =>
    void navigate({
      search: {
        role: roleId ?? undefined,
        with: compareWithIds.length > 0 ? compareWithIds : undefined,
      },
      replace: true,
    });

  return (
    <Flex direction="column" className={styles.container}>
      <RoleComparePage
        roleId={search.role ?? null}
        compareWithIds={search.with ?? EMPTY_IDS}
        onSelectionChange={handleSelectionChange}
      />
    </Flex>
  );
}
